import React, { useState } from 'react';

// Styles & Assets
import styles from './AlertRule.module.css';
import { RxCross1 } from 'react-icons/rx';

// Components
import SliderCheckbox from '../../UI/SliderCheckbox/SliderCheckbox';
import InputSelect from '../../UI/InputSelect/InputSelect';
import Input from '../../UI/Input/Input';

// Language
import { useTranslation } from 'react-i18next';

// Constants
import { OPTIONS_COMPARISON } from '../../../data/ConstantsScores';

// Interface
import { IAlertRule } from '../../../interfaces/Score';
import { AlertRuleDispatch } from '../../../interfaces/Reducers';
import { OptionsSelect } from '../../../interfaces/Input';
import { PollQuestion } from '../../../interfaces/Poll';
interface Props {
    rule: IAlertRule
    questions: PollQuestion[]
    dispatchAlertRules: React.Dispatch<AlertRuleDispatch>
}

const AlertRule: React.FC<Props> = ({ rule, questions, dispatchAlertRules }) => {

    const { t } = useTranslation();

    const OPTIONS_CHECK_BEFORE = [{ id: 0, name: t('is') }, { id: 1, name: t('varies') }];
    const [checkBefore, setCheckBefore] = useState(rule.check_before ?? false);

    const OPTIONS_DURING = [{ id: 0, name: t('-') }, { id: 1, name: t('for') }];
    const [duringStr, setDuringStr] = useState(rule?.consecutive === undefined || rule?.consecutive <= 1 ? 0 : 1);

    const [answerIDSelected, setAnswerIDSelected] = useState<number | string>(-1);

    const handleChangeEnabled = (e: React.ChangeEvent<HTMLInputElement>) =>
        dispatchAlertRules({ type: 'CHANGE_ENABLED', enabled: e.target.checked, id: rule.id });

    const handleCheckBefore = (option: OptionsSelect) => {
        setCheckBefore(Boolean(option.id));
        dispatchAlertRules({ type: 'CHANGE_CHECK_BEFORE', id: rule.id, check_before: Boolean(option.id) });
    };

    const handleChangeQuestion = (option: OptionsSelect) => {
        if (option.id === -1) setAnswerIDSelected(-1);
        dispatchAlertRules({
            type: 'CHANGE_QUESTION_ID', id: rule.id,
            inquiry_question_id: option.id === -1 ? null : Number(option.id)
        });
    };

    const NaNorNumber = (str: string) => str.trim() === '' ? NaN : Number(str);

    const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>) =>
        dispatchAlertRules({ type: 'CHANGE_VALUE', value: NaNorNumber(e.target.value), id: rule.id });

    const handleChangeConsecutive = (e: React.ChangeEvent<HTMLInputElement>) =>
        dispatchAlertRules({ type: 'CHANGE_CONSECUTIVE', consecutive: NaNorNumber(e.target.value), id: rule.id });

    const handleChangeComparison = (option: OptionsSelect) => {
        const comparison = OPTIONS_COMPARISON.find(c => c.id === option.id)?.name;

        if (comparison !== undefined) dispatchAlertRules({ type: 'CHANGE_COMPARISON', comparison, id: rule.id });
    };

    const handleChangeDuring = (option: OptionsSelect) => {
        if (duringStr === 0 && option.id === 1)
            dispatchAlertRules({ type: 'CHANGE_CONSECUTIVE', consecutive: 2, id: rule.id });
        if (duringStr === 1 && option.id === 0)
            dispatchAlertRules({ type: 'CHANGE_CONSECUTIVE', consecutive: 1, id: rule.id });
        setDuringStr(Number(option.id));
    };

    const handleChangeAnswer = (option: OptionsSelect) => {
        const answerFound = questionFound?.answers.find(a => a.id === option.id);
        if (option.id === -1) {
            setAnswerIDSelected(-1);
            return;
        }
        if (answerFound?.value !== undefined && answerFound.value !== null)
            dispatchAlertRules({ type: 'CHANGE_VALUE', value: answerFound.value, id: rule.id });
        if (answerFound?.id !== undefined) setAnswerIDSelected(answerFound.id);
    };

    const deleteAlert = () => dispatchAlertRules({ type: 'DELETE_RULE', id: rule.id });

    const DEFAULT_GLOBAL = { id: -1, name: t('global_value') };
    const questionValues = [DEFAULT_GLOBAL, ...questions.filter(q => q.inquiry_question_type_id !== 3).map(q => ({ id: q.id || q.id || -1, name: q.description || '' }))];

    const questionFound = questions.find(q => q.id === rule.inquiry_question_id);

    const DEFAULT_ANSWER = { id: -1, name: t('value') };
    const answers = questionFound ? [DEFAULT_ANSWER, ...questionFound.answers.map(a => ({ id: a.id || a.id || -1, name: a.description || '' }))] : null;

    const DEFAULT_QUESTION_DESCRIPTION = rule.inquiry_question_id === null ?
        DEFAULT_GLOBAL.name : questionFound?.description;

    return (
        <div className={styles.ruleContainer}>
            <button className={styles.cross} onClick={deleteAlert}>
                <RxCross1 />
            </button>
            <SliderCheckbox
                label=''
                defaultChecked={rule.enabled}
                handleChange={handleChangeEnabled}
            />
            <p>{t('if_value_of')}</p>
            <InputSelect
                options={questionValues}
                onChange={handleChangeQuestion}
                value={DEFAULT_QUESTION_DESCRIPTION || ''}
                label=''
                simpleStyles
            />
            <div style={{ width: '80px' }}>
                <InputSelect
                    options={OPTIONS_CHECK_BEFORE}
                    onChange={handleCheckBefore}
                    label=''
                    value={OPTIONS_CHECK_BEFORE.find(val => val.id === Number(checkBefore))?.name || ''}
                    simpleStyles
                />
            </div>
            <div style={{ width: '60px' }}>
                <InputSelect
                    options={OPTIONS_COMPARISON}
                    onChange={handleChangeComparison}
                    value={rule.comparison}
                    label=''
                    simpleStyles
                />
            </div>
            <p>{t('than')}</p>
            {answers && !checkBefore &&
                <InputSelect
                    options={answers}
                    onChange={handleChangeAnswer}
                    value={answers.find(a => a.id === answerIDSelected)?.name || ''}
                    label=''
                    simpleStyles
                />
            }
            {(answerIDSelected === -1 || checkBefore) &&
                <div style={{ width: '88px' }}>
                    <Input
                        type='number'
                        defaultValue={Number.isNaN(rule.value) ? '' : rule.value}
                        onChange={handleChangeValue}
                        error={isNaN(rule.value)}
                    />
                </div>
            }
            {!checkBefore ?
                <div style={{ width: '110px' }}>
                    <InputSelect
                        options={OPTIONS_DURING}
                        onChange={handleChangeDuring}
                        value={OPTIONS_DURING.find(val => val.id === duringStr)?.name || ''}
                        label=''
                        simpleStyles
                    />
                </div>
                :
                <p>{t('for')}</p>
            }
            {(duringStr === 1 || checkBefore) &&
                <>
                    <div style={{ width: '88px' }}>
                        <Input
                            type='number'
                            defaultValue={Number.isNaN(rule.consecutive) ? '' : rule.consecutive}
                            onChange={handleChangeConsecutive}
                            error={isNaN(rule.consecutive) || rule.consecutive < 1 || !Number.isInteger(rule.consecutive)}
                            step={1}
                        />
                    </div>
                    <p>{t('polls_in_a_row')}</p>
                </>
            }
        </div>
    );
};

export default AlertRule;